import {FC, useMemo} from "react";
import {useFlags} from "launchdarkly-react-client-sdk";
import {
  MANAGE_COMPLEX_OPERATIONAL_HIERARCHY,
  MANAGE_SIMPLE_OPERATIONAL_HIERARCHY,
  useAuthorization
} from "../../access";
import {useClientEntity} from "../context";
import {
  isClientEntityOperational,
  clientBranchIsComplex,
  clientBranchIsFullyOperational
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {features} from "../../features";
import {ManageHierarchyButton} from "./button/ManageHierarchyButton";
import {ManageHierarchySplitButton} from "./button/ManageHierarchySplitButton";
import {CreateOperationalEntityButton} from "./CreateOperationalEntityButton";

import "./ClientHierarchyActionButtons.scss";

export interface ClientHierarchyActionButtonsProps {
  /**
   * Optional CSS class name to apply to the button container
   */
  className?: string;
  /**
   * Optional ID to apply to the button container
   */
  id?: string;
}

/**
 * The {@link ClientHierarchyActionButtons} component encapsulates the logic
 * for determining which action buttons (Manage Hierarchy, Create Entity, etc.)
 * should be displayed based on user permissions, hierarchy complexity,
 * and entity operational status.
 *
 * This component consolidates the button switching logic that was previously
 * duplicated across multiple tab components.
 */
export const ClientHierarchyActionButtons: FC<
  ClientHierarchyActionButtonsProps
> = ({
  className = "client-hierarchy-overview-tab-button",
  id = "client-hierarchy-overview-tab-button"
}) => {
  const flags = useFlags();
  const clientEntity = useClientEntity();
  const granted = useAuthorization();

  const canManageSimpleHierarchy = useMemo<boolean>(
    () => granted([MANAGE_SIMPLE_OPERATIONAL_HIERARCHY]),
    [granted]
  );

  const canManageComplexHierarchy = useMemo<boolean>(
    () => granted([MANAGE_COMPLEX_OPERATIONAL_HIERARCHY]),
    [granted]
  );

  const {entity} = clientEntity;
  const branchIsComplex = clientBranchIsComplex(entity.hierarchy!, entity);
  const isOperationalEntity = isClientEntityOperational(entity);
  const branchIsFullyOperational = clientBranchIsFullyOperational(
    entity.hierarchy!,
    entity
  );

  // Don't render anything if feature flag is disabled
  if (
    !flags[features.pdt.createSimpleOperationalHierarchy.manageHierarchyButton]
  ) {
    return null;
  }

  /**
   * Determines which button(s) to render based on permissions and entity state.
   *
   * Logic:
   * 1. If user has both permissions:
   *    - If entity is complex OR operational → show "Create Entity"
   *    - If all entities in the hierarchy are operational, show Create Entity button
   *    - Otherwise → show split button (both options)
   * 2. If user has only simple permission:
   *    - If entity is NOT operational AND NOT complex → show "Manage Hierarchy"
   *    - Otherwise → show nothing
   * 3. If user has no permissions → show nothing
   *
   */
  const renderButtons = () => {
    if (canManageSimpleHierarchy && canManageComplexHierarchy) {
      if (branchIsComplex || isOperationalEntity || branchIsFullyOperational) {
        return <CreateOperationalEntityButton />;
      } else {
        return <ManageHierarchySplitButton />;
      }
    } else if (
      canManageSimpleHierarchy &&
      !canManageComplexHierarchy &&
      !isOperationalEntity
    ) {
      if (!branchIsComplex) {
        return <ManageHierarchyButton />;
      }
    }
    return null;
  };

  return (
    <div id={id} className={className}>
      {renderButtons()}
    </div>
  );
};
